import React, { createContext, useContext, useCallback, useEffect, useMemo, useState } from 'react';
import { translations } from './translations';

/*
 * Lightweight i18n for the PUBLIC (logged-out) pages only. Hebrew is PRIMARY (default) and the
 * FALLBACK — a missing 'en' key resolves to the Hebrew string, never a raw key. English is an
 * added option. The internal management app does NOT consume this context; its RTL/Hebrew is
 * unaffected (the dir switch is scoped to the public shell's own root div, never <html>).
 */

const STORAGE_KEY = 'hoperLocale';
const DEFAULT_LOCALE = 'he';
const SUPPORTED = ['he', 'en'];

const I18nContext = createContext(null);

function readInitialLocale() {
    try {
        const v = localStorage.getItem(STORAGE_KEY);
        if (v && SUPPORTED.includes(v)) return v;
    } catch (e) { /* localStorage unavailable — fall through to default */ }
    return DEFAULT_LOCALE;
}

// Resolve a key path (e.g. "nav.discover") against a locale dict, with he fallback.
function lookup(locale, key) {
    const cur = translations[locale] && translations[locale][key];
    if (cur != null) return cur;
    const fallback = translations[DEFAULT_LOCALE] && translations[DEFAULT_LOCALE][key];
    if (fallback != null) return fallback;
    return key; // last resort — should never happen for keys defined in he
}

// Simple {var} interpolation.
function interpolate(str, vars) {
    if (!vars || typeof str !== 'string') return str;
    return str.replace(/\{(\w+)\}/g, (m, name) => (vars[name] != null ? String(vars[name]) : m));
}

export function I18nProvider({ children }) {
    const [locale, setLocaleState] = useState(readInitialLocale);

    const setLocale = useCallback((next) => {
        const val = SUPPORTED.includes(next) ? next : DEFAULT_LOCALE;
        setLocaleState(val);
        try { localStorage.setItem(STORAGE_KEY, val); } catch (e) { /* ignore */ }
    }, []);

    useEffect(() => {
        try { localStorage.setItem(STORAGE_KEY, locale); } catch (e) { /* ignore */ }
    }, [locale]);

    const t = useCallback((key, vars) => interpolate(lookup(locale, key), vars), [locale]);

    const dir = locale === 'he' ? 'rtl' : 'ltr';
    // BCP-47 tag for Intl / toLocaleDateString.
    const dateLocale = locale === 'he' ? 'he-IL' : 'en-US';

    const value = useMemo(() => ({ locale, setLocale, t, dir, dateLocale }), [locale, setLocale, t, dir, dateLocale]);

    return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
    const ctx = useContext(I18nContext);
    if (!ctx) {
        // Defensive: if a public component is ever rendered outside the provider, degrade to
        // Hebrew rather than crash. Keeps the internal app safe too.
        return {
            locale: DEFAULT_LOCALE,
            setLocale: () => {},
            t: (key, vars) => interpolate(lookup(DEFAULT_LOCALE, key), vars),
            dir: 'rtl',
            dateLocale: 'he-IL',
        };
    }
    return ctx;
}

// Format a YYYY-MM-DD string with the active locale. Falls back to the raw input on bad data.
export function formatDate(ymd, dateLocale, opts) {
    try {
        const d = new Date(`${ymd}T00:00:00`);
        if (isNaN(d.getTime())) return ymd;
        return d.toLocaleDateString(dateLocale || 'he-IL', opts || { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
    } catch (e) { return ymd; }
}

export default I18nContext;
